import { z } from "zod";
import {
  AGENT_LIMITS,
  AI_AGENTS,
  DEFAULT_AI_AGENT_CONFIG,
  SCHEDULED_AGENTS,
  validCollectionSteps,
  type AiAgent,
  type AnomalyKind,
  type CollectionTone,
  type ProductDraft,
  type ScheduledAgent,
  type SmartForecast,
} from "@cx/domain";
import { ulidParam } from "./ids";

// ===== Agentes de IA (T3.7, 06-ia-agentes §3) =====

const int = (what: string, min: number, max: number) =>
  z
    .number(`Informe ${what}`)
    .int(`${what[0]!.toUpperCase()}${what.slice(1)} inválido`)
    .min(min, `Use de ${min} a ${max}`)
    .max(max, `Use de ${min} a ${max}`);

/** Configuração dos agentes (PUT /ai/agents/settings e o JSON guardado no DO). Ausente = padrão. */
export const aiAgentConfigInput = z.object({
  planner: z
    .object({
      enabled: z.boolean().default(false),
      coverDays: int("a cobertura", 1, 180).default(DEFAULT_AI_AGENT_CONFIG.planner.coverDays),
      leadTimeDays: int("o prazo de entrega", 1, 120).default(DEFAULT_AI_AGENT_CONFIG.planner.leadTimeDays),
      warehouseId: ulidParam.nullish().transform((v) => v ?? null),
    })
    .prefault({}),
  collector: z
    .object({
      enabled: z.boolean().default(false),
      steps: z
        .array(z.number().int("Dia inválido"), "Informe os passos da régua")
        .default([...DEFAULT_AI_AGENT_CONFIG.collector.steps])
        .refine(validCollectionSteps, "Até 8 passos, de −30 a 90 dias, sem repetir")
        .transform((s) => [...s].sort((a, b) => a - b)),
    })
    .prefault({}),
  sentinel: z
    .object({
      enabled: z.boolean().default(false),
      stockoutDays: int("os dias de ruptura", 1, 60).default(DEFAULT_AI_AGENT_CONFIG.sentinel.stockoutDays),
      channelDropPct4: int("a queda do canal", 50_000, 1_000_000).default(
        DEFAULT_AI_AGENT_CONFIG.sentinel.channelDropPct4,
      ),
      channelMinBaseCents: int("a base mínima do canal", 0, 10_000_000_000).default(
        DEFAULT_AI_AGENT_CONFIG.sentinel.channelMinBaseCents,
      ),
      discountSigma: int("o desvio do desconto", 1, 6).default(
        DEFAULT_AI_AGENT_CONFIG.sentinel.discountSigma,
      ),
      discountMinGapPct4: int("a distância mínima do desconto", 0, 1_000_000).default(
        DEFAULT_AI_AGENT_CONFIG.sentinel.discountMinGapPct4,
      ),
    })
    .prefault({}),
  dailyLlmCalls: int("o limite diário", 0, AGENT_LIMITS.maxDailyLlmCalls).default(
    DEFAULT_AI_AGENT_CONFIG.dailyLlmCalls,
  ),
});
export type AiAgentConfigInput = z.output<typeof aiAgentConfigInput>;

export const aiAgentParam = z.object({ agent: z.enum(AI_AGENTS, "Agente inválido") });
export const scheduledAgentParam = z.object({ agent: z.enum(SCHEDULED_AGENTS, "Agente inválido") });

export const AI_SUGGESTION_STATUSES = ["pending", "accepted", "rejected", "expired"] as const;
type AiSuggestionStatus = (typeof AI_SUGGESTION_STATUSES)[number];

export const aiAgentSuggestionQuery = z.object({
  agent: z.enum(AI_AGENTS, "Agente inválido").optional(),
  status: z.enum([...AI_SUGGESTION_STATUSES, "all"], "Situação inválida").default("pending"),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type AiAgentSuggestionQuery = z.output<typeof aiAgentSuggestionQuery>;

/** Texto colado no Cadastro inteligente (a planilha vai em multipart, campo `file`). */
export const productEnrichTextInput = z.object({
  text: z
    .string("Cole o texto do catálogo")
    .trim()
    .min(3, "Cole o texto do catálogo")
    .max(AGENT_LIMITS.enricherChars, `Até ${AGENT_LIMITS.enricherChars} caracteres`),
});
export type ProductEnrichTextInput = z.output<typeof productEnrichTextInput>;

// ---------- propostas gravadas em ai_suggestions.proposal_json ----------

const q4 = z.number().int();
const cents = z.number().int().min(0);

export const plannerItemProposal = z.object({
  productId: ulidParam,
  name: z.string().max(200),
  sku: z.string().max(60).nullable(),
  unit: z.string().max(6),
  qtyQ4: q4.min(1),
  unitPriceCents: cents,
  totalCents: cents,
  method: z.enum(["holt", "min_max"]),
  availableQ4: q4,
  onOrderQ4: q4,
  dailyForecastQ4: q4,
  reorderPointQ4: q4,
  safetyQ4: q4,
  stockMinQ4: q4.nullable(),
  stockMaxQ4: q4.nullable(),
  coverageDays: z.number().int().nullable(),
  trend: z.enum(["up", "down", "flat"]),
});
export const plannerProposal = z.object({
  kind: z.literal("purchase_order"),
  supplierId: ulidParam,
  supplierName: z.string().max(200),
  warehouseId: ulidParam.nullable(),
  leadTimeDays: z.number().int(),
  coverDays: z.number().int(),
  items: z.array(plannerItemProposal).min(1).max(AGENT_LIMITS.plannerItemsPerSuggestion),
  totalCents: cents,
});
export type PlannerProposal = z.infer<typeof plannerProposal>;

export const collectorProposal = z.object({
  kind: z.literal("collection_email"),
  titleId: ulidParam,
  contactId: ulidParam,
  contactName: z.string().max(200),
  toEmail: z.string().max(254),
  documentNumber: z.string().max(60).nullable(),
  step: z.number().int(),
  stepLabel: z.string().max(60),
  tone: z.enum(["lembrete", "vencimento", "cordial", "firme"]) satisfies z.ZodType<CollectionTone>,
  daysLate: z.number().int(),
  dueDate: z.string().max(10),
  amountCents: cents,
  history: z.object({ paidCount: z.number().int(), avgDaysLate: z.number().int() }),
  subject: z.string().max(200),
  /** Parágrafo pessoal (modelo ou texto padrão), já limpo. */
  text: z.string().max(AGENT_LIMITS.collectorTextMax),
  /** Bloco do código: valor, vencimento, documento, Pix copia e cola, linha digitável. */
  paymentLines: z.array(z.string().max(600)).max(10),
  pixChargeId: ulidParam.nullable(),
  boletoId: ulidParam.nullable(),
});
export type CollectorProposal = z.infer<typeof collectorProposal>;

export const ANOMALY_KINDS = [
  "negative_margin",
  "discount_outlier",
  "stockout_risk",
  "channel_drop",
] as const;
export const anomalyProposal = z.object({
  kind: z.literal("anomaly"),
  anomaly: z.enum(ANOMALY_KINDS) satisfies z.ZodType<AnomalyKind>,
  title: z.string().max(200),
  /** Registro afetado (para o link na tela). */
  subject: z.object({
    type: z.enum(["product", "sales_order", "channel"]),
    id: z.string().max(60),
    label: z.string().max(200),
  }),
  /** Números do alerta (centavos, q4, pct4, dias) com o rótulo em pt-BR. */
  metrics: z.array(
    z.object({
      label: z.string().max(80),
      kind: z.enum(["cents", "q4", "pct4", "int"]),
      value: z.number().int().nullable(),
    }),
  ),
  /** Precisa de products:read_cost / sellers:read para ver. */
  needsCost: z.boolean(),
  needsSellers: z.boolean(),
  day: z.string().max(10),
});
export type AnomalyProposal = z.infer<typeof anomalyProposal>;

export const enricherProposal = z.object({
  kind: z.literal("product"),
  source: z.enum(["text", "sheet"]),
  draft: z.object({
    name: z.string().min(1).max(120),
    sku: z.string().max(60).nullable(),
    shortDescription: z.string().max(255).nullable(),
    longDescription: z.string().max(4000).nullable(),
    unit: z.string().max(6),
    gtin: z.string().max(14).nullable(),
    ncm: z.string().max(8).nullable(),
    ncmConfidence: z.number().min(0).max(1).nullable(),
    cest: z.string().max(7).nullable(),
    brand: z.string().max(60).nullable(),
    attributes: z.array(z.object({ name: z.string().max(40), value: z.string().max(80) })).max(10),
    priceCents: cents.nullable(),
    costCents: cents.nullable(),
    review: z.array(z.string().max(20)).max(10),
  }) satisfies z.ZodType<ProductDraft>,
});
export type EnricherProposal = z.infer<typeof enricherProposal>;

export const agentProposal = z.discriminatedUnion("kind", [
  plannerProposal,
  collectorProposal,
  anomalyProposal,
  enricherProposal,
]);
export type AgentProposal = z.infer<typeof agentProposal>;

export type AgentResult =
  | { kind: "purchase_order"; purchaseOrderId: string; number: number }
  | {
      kind: "collection_email";
      messageId: string;
      status: "queued" | "sending" | "sent" | "failed" | "skipped";
    }
  | { kind: "product"; productId: string }
  | { kind: "acknowledged" };

export type AiAgentSuggestionView = {
  id: string;
  agent: AiAgent;
  status: AiSuggestionStatus;
  rationale: string | null;
  confidencePct4: number | null;
  createdAt: string;
  decidedAt: string | null;
  proposal: AgentProposal;
  result: AgentResult | null;
  /** Mensagem do Cobrador: situação do envio (atualizada pela fila). */
  delivery: {
    status: "queued" | "sending" | "sent" | "failed" | "skipped";
    error: string | null;
    sentAt: string | null;
  } | null;
};
export type AiAgentSuggestionPage = { data: AiAgentSuggestionView[]; nextCursor: string | null };

export type AiAgentRunView = {
  id: string;
  agent: AiAgent;
  trigger: "cron" | "manual";
  status: "running" | "done" | "failed";
  startedAt: string;
  finishedAt: string | null;
  createdCount: number;
  llmUsed: boolean;
  summary: Record<string, number> | null;
  error: string | null;
};

export type AiAgentStats = {
  created: number;
  accepted: number;
  rejected: number;
  expired: number;
  pending: number;
  /** aceitas ÷ decididas (aceitas + recusadas), pct4; null sem decisão. */
  acceptRatePct4: number | null;
};

export type AiAgentsOverview = {
  config: AiAgentConfigInput;
  usageToday: { llmCalls: number; limit: number };
  agents: { agent: AiAgent; lastRun: AiAgentRunView | null; stats30d: AiAgentStats }[];
  runs: AiAgentRunView[];
};

export type AiAgentRunResult = {
  run: AiAgentRunView;
  created: number;
};

export type AgentDecisionResult = {
  suggestion: AiAgentSuggestionView;
};

export type SmartForecastResult = {
  forecast: SmartForecast | null;
  explanation: string;
  llm: boolean;
};

export type ScheduledAgentId = ScheduledAgent;

/** O que o usuário vê dos agentes (calculado na rota pelas permissões; nunca vem do cliente). */
export type AgentGrants = {
  /** purchase_orders:read (o DO exige também `cost` para o Planejador) */
  planner: boolean;
  /** receivables:read */
  collector: boolean;
  /** dashboard:read */
  anomaly: boolean;
  /** products:read_cost (alerta de margem, preços do Planejador e custo do rascunho do Cadastro inteligente) */
  cost: boolean;
  /** sellers:read (alerta de desconto por vendedor) */
  anomalySellers: boolean;
  /** products:create */
  enricher: boolean;
};
